import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, Badge } from '@tremor/react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { KPICard } from '@/components/KPICard';
import { api, extractErrorMessage } from '@/lib/api';
import {
  formatRangeShort,
  useDateRange,
  useDateRangeKey,
} from '@/lib/dateRange';

interface WindowKpis {
  active_users: number;
  revenue_brl: number;
  purchases: number;
  paying_orgs: number;
  cases: number;
  cases_done: number;
  success_rate: number;
  new_users: number;
  // [ASSISTIDO 22/10] contas que NÓS abrimos e o cliente ainda não acessou:
  // ficam FORA de new_users (não são aquisição) até o 1º login dele.
  new_users_assistidos: number;
  ai_calls: number;
}

interface OverviewKpis {
  status: string;
  phase: number;
  as_of: string;
  window: { start: string; end: string; days: number };
  kpis: WindowKpis;
  previous: WindowKpis | null;
  deltas: Record<string, number | null> | null;
  stickiness: {
    dau: number;
    wau: number;
    mau: number;
    stickiness_pct: number | null;
  };
  notes?: Record<string, string>;
}

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtBrl(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(n);
}

function fmtPct(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtPctRaw(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${n.toFixed(decimals)}%`;
}

function DeltaBadge({ pct }: { pct: number | null | undefined }) {
  if (pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
        <Minus className="h-3 w-3" /> sem comparação
      </span>
    );
  }
  if (pct === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
        <Minus className="h-3 w-3" /> 0% vs anterior
      </span>
    );
  }
  const positive = pct > 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  const color = positive ? 'text-emerald-400' : 'text-red-400';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${color}`}>
      <Icon className="h-3 w-3" />
      {positive ? '+' : ''}
      {pct.toFixed(1)}% vs anterior
    </span>
  );
}

export function OverviewPage() {
  const { range, apiParams } = useDateRange();
  const rangeKey = useDateRangeKey();

  const { data, isLoading, error } = useQuery<OverviewKpis>({
    queryKey: ['overview.kpis', rangeKey],
    queryFn: async () =>
      (await api().get('/overview/kpis', { params: apiParams })).data,
    refetchInterval: 60_000,
  });

  const errMsg = error ? extractErrorMessage(error, 'erro ao carregar KPIs') : null;
  const k = data?.kpis;
  const d = data?.deltas;
  const s = data?.stickiness;

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <Title>Overview</Title>
          {data && (
            <Badge color="emerald" size="sm">
              {formatRangeShort(range)} — atualizado{' '}
              {new Date(data.as_of).toLocaleTimeString('pt-BR')}
            </Badge>
          )}
        </div>
        <Text>
          KPIs da plataforma JusQuant para o período selecionado. Comparação
          automática com o período anterior equivalente.
        </Text>
      </header>

      {/* Linha 1: Engagement (window-bound + rolling reference) */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          Engagement no período · {formatRangeShort(range)}
        </Text>
        <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
          <KPICard
            label="Visitantes únicos"
            value={isLoading ? null : fmtInt(k?.active_users)}
            loading={isLoading}
            error={errMsg}
            hint="visitor_id distintos no período"
            footer={<DeltaBadge pct={d?.active_users_delta_pct} />}
          />
          <KPICard
            label="Novos usuários"
            value={isLoading ? null : fmtInt(k?.new_users)}
            loading={isLoading}
            error={errMsg}
            hint={
              k?.new_users_assistidos
                ? `cadastros adquiridos · assistidos: ${fmtInt(k.new_users_assistidos)}`
                : 'cadastros no período'
            }
            footer={<DeltaBadge pct={d?.new_users_delta_pct} />}
          />
          <KPICard
            label="Casos rodados"
            value={isLoading ? null : fmtInt(k?.cases)}
            loading={isLoading}
            error={errMsg}
            hint={`${fmtInt(k?.cases_done)} concluídos`}
            footer={<DeltaBadge pct={d?.cases_delta_pct} />}
          />
          <KPICard
            label="Sucesso"
            value={isLoading ? null : fmtPct(k?.success_rate)}
            loading={isLoading}
            error={errMsg}
            hint="% que geraram PDF"
          />
        </Grid>
      </section>

      {/* Linha 2: Receita */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          Receita no período
        </Text>
        <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
          <KPICard
            label="Receita"
            value={isLoading ? null : fmtBrl(k?.revenue_brl)}
            loading={isLoading}
            error={errMsg}
            hint="créditos pagos no período"
            footer={<DeltaBadge pct={d?.revenue_brl_delta_pct} />}
          />
          <KPICard
            label="Compras"
            value={isLoading ? null : fmtInt(k?.purchases)}
            loading={isLoading}
            error={errMsg}
            hint="transações de compra"
            footer={<DeltaBadge pct={d?.purchases_delta_pct} />}
          />
          <KPICard
            label="Orgs pagantes"
            value={isLoading ? null : fmtInt(k?.paying_orgs)}
            loading={isLoading}
            error={errMsg}
            hint="organizações distintas"
            footer={<DeltaBadge pct={d?.paying_orgs_delta_pct} />}
          />
          <KPICard
            label="Chamadas LLM"
            value={isLoading ? null : fmtInt(k?.ai_calls)}
            loading={isLoading}
            error={errMsg}
            hint="extraction_event do período"
            footer={<DeltaBadge pct={d?.ai_calls_delta_pct} />}
          />
        </Grid>
      </section>

      {/* Linha 3: Stickiness referência (DAU/WAU/MAU rolling, independente da janela) */}
      <section className="space-y-2">
        <Text className="text-xs uppercase tracking-wide text-slate-400">
          Stickiness — referência rolling (independe do período)
        </Text>
        <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
          <KPICard
            label="DAU"
            value={isLoading ? null : fmtInt(s?.dau)}
            loading={isLoading}
            error={errMsg}
            hint="hoje (UTC)"
          />
          <KPICard
            label="WAU"
            value={isLoading ? null : fmtInt(s?.wau)}
            loading={isLoading}
            error={errMsg}
            hint="últimos 7 dias"
          />
          <KPICard
            label="MAU"
            value={isLoading ? null : fmtInt(s?.mau)}
            loading={isLoading}
            error={errMsg}
            hint="últimos 30 dias"
          />
          <KPICard
            label="DAU/MAU"
            value={isLoading ? null : fmtPctRaw(s?.stickiness_pct)}
            loading={isLoading}
            error={errMsg}
            hint="quanto + alto, mais grudento"
          />
        </Grid>
      </section>

      {data?.notes && Object.keys(data.notes).length > 0 && (
        <Card className="border border-amber-700/40 bg-amber-900/10">
          <div className="space-y-1 text-sm">
            <Text className="font-medium text-amber-200">
              Pendências de instrumentação
            </Text>
            {Object.entries(data.notes).map(([key, v]) => (
              <p key={key} className="text-amber-100/80">
                <code className="rounded bg-slate-800 px-1 py-0.5 text-xs">
                  {key}
                </code>
                : {v}
              </p>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
